import { workspaceRoleLabel } from '@/utils/workspaceRoleLabel';
import { useState } from 'react';
import { Check, ChevronDown, Plus, Trash2, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useActivateWorkspace, useFetchMyInvitations } from '@/hooksApi';
import { useUserStore } from '@/stores';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import type { Workspace } from '@/types';
import CreateWorkspaceDialog from './CreateWorkspaceDialog';
import DeleteWorkspaceDialog from './DeleteWorkspaceDialog';
import InvitationsPanel from './InvitationsPanel';
import InviteMemberDialog from './InviteMemberDialog';
import SentInvitations from './SentInvitations';
import WorkspaceNameEditor from './WorkspaceNameEditor';
import WorkspaceTile from './WorkspaceTile';
import { duplicateNameTags } from './duplicateNameTags';

const CARD = 'rounded-xl border border-sd-border bg-sd-card shadow-sm';

/**
 * BOXY-44 §6 — the Workspaces page.
 *
 * Section order is deliberate (addendum §8): INVITATIONS TO YOU FIRST, and only
 * when there are any. They expire in seven days, so the one time-limited thing
 * on the page goes above the fold; the rest of the page is reference material
 * that will still be there tomorrow.
 *
 * ⭐ NOT PERMISSION-GATED as a route. An invitation is addressed to a PERSON,
 * and the invitee usually holds nothing in the workspace inviting them — a gate
 * would hide the page from exactly the people it exists for. Individual ACTIONS
 * are gated instead, which is also how the server does it.
 */
const WorkspacesSection = () => {
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);
  const activate = useActivateWorkspace();
  const { data: invitationsData } = useFetchMyInvitations();

  const [creating, setCreating] = useState(false);
  const [inviteTo, setInviteTo] = useState<Workspace | null>(null);
  const [deleting, setDeleting] = useState<Workspace | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const memberships = user?.workspaces ?? [];
  const invitations = invitationsData?.object ?? [];
  const activeUid = user?.active_workspace ?? memberships.find((w) => w.is_active)?.uid;
  const ownsActive = memberships.find((w) => w.uid === activeUid)?.role === 'OWNER';

  // Active pinned FIRST — it is the workspace the merchant is reasoning about,
  // and the row they are most likely to act on. Server order is company id.
  const workspaces = (() => {
    const active = memberships.find((w) => w.uid === activeUid);
    const rest = memberships
      .filter((w) => w.uid !== activeUid)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    return active ? [active, ...rest] : rest;
  })();
  const tags = duplicateNameTags(memberships);
  const canCreate = Boolean(user?.can_create_workspaces) && ownsActive;

  const newWorkspaceButton = (
    <Button size='sm' disabled={!canCreate} onClick={() => setCreating(true)}>
      <Plus className='h-4 w-4' aria-hidden />
      {String(t('workspace.new'))}
    </Button>
  );

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='mx-auto w-full max-w-3xl space-y-8 p-6'>
        <SettingsBreadcrumb moduleName='workspace.plural' moduleRoute={ROUTE.SETTINGS_WORKSPACES} />

        <header className='flex items-start justify-between gap-4'>
          <div className='space-y-1'>
            <h1 className='text-[22px] font-semibold leading-7 text-sd-foreground'>
              {String(t('workspace.plural'))}
            </h1>
            <p className='text-sm leading-relaxed text-sd-muted-foreground'>
              {String(t('workspace.page_subtitle'))}
            </p>
          </div>

          {/* Always rendered, disabled with the reason — never hidden. */}
          {canCreate ? (
            newWorkspaceButton
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='shrink-0'>{newWorkspaceButton}</span>
                </TooltipTrigger>
                <TooltipContent>{String(t('workspace.ask_boxy'))}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </header>

        {/* Time-limited, so first — and absent entirely when there is nothing
            to act on, rather than an empty section everyone scrolls past. */}
        {invitations.length > 0 && (
          <section id='invitations' className='space-y-3'>
            <div className='space-y-1'>
              <h2 className='text-sm font-semibold text-sd-foreground'>
                {String(t('workspace.invitations_for_you'))}
              </h2>
              <p className='text-[13px] leading-relaxed text-sd-muted-foreground'>
                {String(t('workspace.invitations_description'))}
              </p>
            </div>
            <InvitationsPanel />
          </section>
        )}

        <section className='space-y-3'>
          <h2 className='text-sm font-semibold text-sd-foreground'>
            {String(t('workspace.my_workspaces'))}
          </h2>

          <ul className='list-none space-y-3 p-0'>
            {workspaces.map((workspace) => {
              const isActive = workspace.uid === activeUid;
              const isOwner = workspace.role === 'OWNER';
              const isExpanded = expanded === workspace.uid;

              return (
                <li
                  key={workspace.uid}
                  className={cn(
                    CARD,
                    'px-5 py-4',
                    /* 🔴 THE ACTIVE WORKSPACE HAS TO BE OBVIOUS FROM ACROSS THE
                       ROOM. Owner, 2026-09-16: the active style should "be
                       green with checkmarks and clear". Before this, the only
                       difference between the workspace you are IN and the six
                       you are not was a small outlined pill — the cards were
                       otherwise identical, on a page whose entire job is to
                       tell you where you are. Green ground, green border, a
                       filled check. */
                    isActive &&
                      'border-customGreen bg-customGreen/[0.06] ring-1 ring-customGreen/40',
                  )}
                >
                  <div className='flex flex-wrap items-center gap-3'>
                    <WorkspaceTile uid={workspace.uid} name={workspace.name} size='lg' />

                    <div className='min-w-0 flex-1'>
                      {/* The name, the active check, and — for its owner — the
                          pencil that edits it in place. */}
                      <WorkspaceNameEditor
                        workspace={workspace}
                        isOwner={isOwner}
                        isActive={isActive}
                      />
                      <p className='text-xs text-sd-muted-foreground'>
                        {workspaceRoleLabel(workspace, t)}
                        {tags[workspace.uid] ? (
                          <>
                            {' · '}
                            <bdi dir='ltr'>{tags[workspace.uid]}</bdi>
                          </>
                        ) : null}
                      </p>
                    </div>

                    {isActive ? (
                      <span className='flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-customGreen px-3 text-[12px] font-semibold text-white'>
                        <Check className='h-3.5 w-3.5' strokeWidth={3} aria-hidden />
                        {String(t('workspace.active'))}
                      </span>
                    ) : (
                      <Button
                        size='sm'
                        variant='secondary'
                        className='h-9 shrink-0'
                        disabled={activate.isLoading}
                        onClick={() => void activate.mutateAsync(workspace.uid)}
                      >
                        {String(t('workspace.switch_to'))}
                      </Button>
                    )}

                    {/* 🔴 ONLY ON THE WORKSPACE YOU ARE IN. Owner, 2026-09-16:
                        "you couldn't be able to invite the members when the
                        workspace is not selected so we should hide the invite
                        members button and only show it when we are on correct
                        in the target workspace."

                        It also closes QA's D15: the button rendered on EVERY
                        card, including workspaces where the caller is a member
                        without USER_MANAGEMENT — there the POST answers 403 and
                        the dialog simply failed. A control that cannot succeed
                        should not be offered.

                        Staffing is a PERMISSION on the server (an ADMIN can
                        hold it), but the payload only carries the membership
                        role — so the button follows the role and the server
                        stays the authority. */}
                    {isOwner && isActive && (
                      <Button
                        size='sm'
                        variant='ghost'
                        className='h-9 shrink-0'
                        onClick={() => setInviteTo(workspace)}
                      >
                        <UserPlus className='h-4 w-4' aria-hidden />
                        {String(t('workspace.invite_member'))}
                      </Button>
                    )}

                    {/* 🔴 RENDERED FOR EVERY CARD, DISABLED WITH A REASON.
                        The owner's standing rule: an unusable control is shown
                        and explained, never hidden — a control that vanishes
                        teaches nothing and reads as a bug when a colleague can
                        see it and you cannot.

                        Three reasons it can be off, and each says which:
                        not the owner; it is the workspace you are standing in
                        (his rule — "merchant couldn't remove the workspace when
                        it's logged in the current workspace"); or it is the
                        only one you have, which is the D14 state this must not
                        be able to create.

                        The SERVER refuses all three independently. This is a
                        courtesy, never the authority. */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* A disabled button fires no pointer events, so the
                              tooltip — which is the whole explanation — needs a
                              live wrapper to hang from. */}
                          <span className='shrink-0'>
                            <Button
                              size='sm'
                              variant='ghost'
                              className='h-9 shrink-0 text-red-600 hover:bg-red-500/10 hover:text-red-700'
                              disabled={!isOwner || isActive || workspaces.length <= 1}
                              aria-label={String(t('workspace.delete'))}
                              onClick={() => setDeleting(workspace)}
                            >
                              <Trash2 className='h-4 w-4' aria-hidden />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {String(
                            t(
                              !isOwner
                                ? 'workspace.delete_owner_only'
                                : workspaces.length <= 1
                                  ? 'workspace.delete_last_tooltip'
                                  : isActive
                                    ? 'workspace.delete_active_tooltip'
                                    : 'workspace.delete',
                            ),
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {isOwner && (
                    <div className='mt-3 border-t border-sd-border pt-2'>
                      <button
                        type='button'
                        className='flex w-full items-center gap-1 text-[13px] text-sd-muted-foreground transition-colors hover:text-sd-foreground'
                        aria-expanded={isExpanded}
                        onClick={() => setExpanded(isExpanded ? null : workspace.uid)}
                      >
                        <ChevronDown
                          className={cn(
                            'h-3.5 w-3.5 transition-transform',
                            isExpanded && 'rotate-180',
                          )}
                          aria-hidden
                        />
                        {String(t('workspace.sent_invitations'))}
                      </button>

                      {/* Mounted only when opened: one request per workspace on
                          page load would be N requests nobody asked for. */}
                      {isExpanded && <SentInvitations workspaceUid={workspace.uid} />}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <CreateWorkspaceDialog open={creating} onOpenChange={setCreating} />

      <DeleteWorkspaceDialog
        workspace={deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      />

      {inviteTo && (
        <InviteMemberDialog
          workspaceUid={inviteTo.uid}
          workspaceName={inviteTo.name}
          open={Boolean(inviteTo)}
          onOpenChange={(next) => !next && setInviteTo(null)}
        />
      )}
    </div>
  );
};

export default WorkspacesSection;
